import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, isRefreshFor } from './AuthContext';
import { readSeq, mergeRows, byLatestMessage } from './whatsappSync';
import { SearchIcon, WhatsAppIcon, MessageIcon, CloseIcon, UserIcon, GlobeIcon, InfoIcon } from './Icons';
import SelectMenu from './SelectMenu';
import CallButton from './CallButton';
import WhatsAppChat from './WhatsAppChat';
import WhatsAppCustomerPanel from './WhatsAppCustomerPanel';
import WhatsAppWallpaperPicker from './WhatsAppWallpaperPicker';
import { getKnownAgents, isInternationalNumber, normalizeNumber } from './callUtils';
import { COUNTRIES, DEFAULT_COUNTRY_ISO2, countryForNumber } from './countryCodes';

// Same reasoning as DailyOrders.js/AbandonedCarts.js's own poll intervals —
// new inbound WhatsApp messages arrive on their own schedule, not on a
// user action, so the thread list has to check back on its own.
const AUTO_REFRESH_MS = 10000;

// The chat list renders LIST_PAGE rows at a time (more on scrolling near
// the bottom). Every row on the page adds to each keystroke's browser work
// in the composer, which matters on slower PCs. Search and filters still
// cover every conversation.
const LIST_PAGE = 100;
const LIST_MORE_WITHIN_PX = 400;

const initials = (name, number) => {
  const source = (name || number || '').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

// Compact "today = time, else short date" stamp for the thread list — same
// idea as WhatsAppChat.js's dayLabel, collapsed to one line since a list row
// has no room for a full date divider.
const listTimestamp = (isoString) => {
  if (!isoString) return '';
  const parsed = new Date(isoString.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return '';
  const isToday = parsed.toDateString() === new Date().toDateString();
  return isToday
    ? parsed.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

// One-line preview of the last message for the thread list — mirrors
// WhatsAppChat.js's WhatsAppBubbleContent switch, but text-only (no actual
// media rendering) since this is just a list row, not the message itself.
const previewText = (conversation) => {
  if (!conversation.last_message_at) return 'No messages yet';
  const outboundPrefix = conversation.last_direction === 'outbound' ? 'You: ' : '';
  switch (conversation.last_message_type) {
    case 'image':
      return `${outboundPrefix}📷 Photo`;
    case 'video':
      return `${outboundPrefix}🎥 Video`;
    case 'document':
      return `${outboundPrefix}📎 Document`;
    case 'sticker':
      return `${outboundPrefix}Sticker`;
    // These usually have a stored one-line summary as body (see the
    // WhatsApp webhook in server/index.js); the labels cover older rows.
    case 'order':
      return `${outboundPrefix}${conversation.last_body || '🛒 Cart'}`;
    case 'contacts':
    case 'contact':
      return `${outboundPrefix}${conversation.last_body || '👤 Contact'}`;
    case 'location':
      return `${outboundPrefix}${conversation.last_body || '📍 Location'}`;
    case 'interactive':
      return `${outboundPrefix}${conversation.last_body || 'Interactive message'}`;
    case 'unsupported':
      return `${outboundPrefix}${conversation.last_body || '⚠️ Unsupported message'}`;
    default:
      return `${outboundPrefix}${conversation.last_body || '(no text)'}`;
  }
};

// Badge text for an international thread — the caller's country (flag +
// name) when the dial code is recognized, plain "International" otherwise.
const internationalLabel = (number) => {
  const country = countryForNumber(number);
  return country ? `${country.flag} ${country.name}` : 'International';
};

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
];

// ~90 characters of a message body around the first match of `term`, with
// the match wrapped in <mark> — for the message-search result rows.
const searchSnippet = (body, term) => {
  const text = String(body || '');
  const index = text.toLowerCase().indexOf(term.toLowerCase());
  if (index < 0) return text.slice(0, 90);
  const start = Math.max(0, index - 30);
  const end = Math.min(text.length, index + term.length + 60);
  return (
    <>
      {start > 0 && '…'}
      {text.slice(start, index)}
      <mark>{text.slice(index, index + term.length)}</mark>
      {text.slice(index + term.length, end)}
      {end < text.length && '…'}
    </>
  );
};

// Pakistan (+92) and Bangladesh (+880): WhatsApp is blocked for them on the
// server (server/whatsappBlocklist.js), so they aren't offered here.
const BLOCKED_CHAT_COUNTRIES = new Set(['PK', 'BD']);
const CHAT_COUNTRIES = COUNTRIES.filter((country) => !BLOCKED_CHAT_COUNTRIES.has(country.iso2));

// Lets an agent open the chat panel for a phone number that has never
// exchanged a WhatsApp message with us — such a number has no row in
// getWhatsappConversations() (it's built from whatsapp_messages, see
// server/db.js), so there'd otherwise be no way to select it in the sidebar.
// Sending itself already works for any number out of the box (see
// POST /api/customers/:number/whatsapp-messages in server/index.js) — this
// modal just gets the chat panel on screen for one.
function NewChatModal({ onClose, onSubmit }) {
  // Full ISO-3166 dial-code list shared with PaymentLinks.js, rather than a
  // hand-curated subset that needed a new entry every time a customer from
  // another country turned up. Keyed by ISO-2, not dial code, since several
  // countries share one (e.g. every NANP member is +1).
  const [countryIso2, setCountryIso2] = useState(DEFAULT_COUNTRY_ISO2);
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const localDigits = number.replace(/\D/g, '');
    if (localDigits.length < 6) {
      setError('Enter a valid phone number.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { dialCode } = CHAT_COUNTRIES.find((c) => c.iso2 === countryIso2) || CHAT_COUNTRIES[0];
      await onSubmit(`+${dialCode}${localDigits}`, name.trim());
    } catch (err) {
      setError(err.message || 'Failed to start chat.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay-center" onClick={onClose}>
      <div className="modal-panel modal-panel-narrow" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Start a new chat</h2>
            <p className="modal-subtitle">
              For a number that hasn't messaged us before — you'll need to send the WhatsApp Greeting template
              first, since there's no open 24h session yet.
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <label className="login-field">
            <span>Phone number</span>
            <div className="login-field-phone-row">
              <select value={countryIso2} onChange={(event) => setCountryIso2(event.target.value)}>
                {CHAT_COUNTRIES.map((country) => (
                  <option key={country.iso2} value={country.iso2}>
                    {country.flag} {country.name} (+{country.dialCode})
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={number}
                onChange={(event) => setNumber(event.target.value)}
                placeholder="e.g. 9876543210"
                autoFocus
                required
              />
            </div>
          </label>
          <label className="login-field">
            <span>Customer name (optional)</span>
            <input type="text" value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          {error && <div className="login-error">{error}</div>}
          <button type="submit" className="btn btn-primary login-submit" disabled={submitting}>
            {submitting ? 'Starting…' : 'Start chat'}
          </button>
        </form>
      </div>
    </div>
  );
}

// WhatsApp overview: every customer thread in one WhatsApp-Web-style split
// view — a searchable thread list on the left (fed by GET
// /api/whatsapp/conversations), the existing two-way chat panel
// (WhatsAppChat, the same component used inside the call-history modal) on
// the right for whichever thread is selected.
// One chat-list row. Memoized because the page re-renders on every call
// event during a live call (and every list poll): with 1,000 rows that
// re-rendered them all, ~48 ms per event. An unchanged row keeps its object
// (mergeRows), so only rows that actually changed render again.
const ConversationRow = React.memo(function ConversationRow({ conversation, active, userId, onSelect }) {
  const unread = conversation.unread_count > 0;
  const international = isInternationalNumber(conversation.number);
  return (
    <button
      type="button"
      className={`whatsapp-overview-item ${active ? 'whatsapp-overview-item-active' : ''}`}
      onClick={() => onSelect(conversation.number)}
    >
      <span className="whatsapp-overview-avatar">{initials(conversation.name, conversation.number)}</span>
      <span className="whatsapp-overview-item-body">
        <span className="whatsapp-overview-item-top">
          <span className={`whatsapp-overview-item-name ${unread ? 'whatsapp-overview-item-name-unread' : ''}`}>
            {conversation.name || conversation.number}
          </span>
          {international && (
            <span
              className={`whatsapp-overview-badge-international ${
                unread ? 'whatsapp-overview-badge-international-unread' : ''
              }`}
              title={internationalLabel(conversation.number)}
            >
              {internationalLabel(conversation.number)}
            </span>
          )}
          <span className="whatsapp-overview-item-time">{listTimestamp(conversation.last_message_at)}</span>
        </span>
        <span className="whatsapp-overview-item-bottom">
          <span className={`whatsapp-overview-item-preview ${unread ? 'whatsapp-overview-item-preview-unread' : ''}`}>
            {previewText(conversation)}
          </span>
          {unread && <span className="whatsapp-overview-unread-badge">{conversation.unread_count}</span>}
        </span>
        {(conversation.status !== 'open' || conversation.assigned_name) && (
          <span className="wa-row-meta">
            {conversation.status && conversation.status !== 'open' && (
              <span className={`wa-status-chip wa-status-chip-${conversation.status}`}>{conversation.status}</span>
            )}
            {conversation.assigned_name && (
              <span className="wa-row-assignee">
                {conversation.assigned_user_id === userId ? 'You' : conversation.assigned_name}
              </span>
            )}
          </span>
        )}
      </span>
    </button>
  );
});

function WhatsAppOverview({ calls = [], onCreateInternationalOrder, openNumber = null, onOpenNumberConsumed }) {
  const { authFetch, subscribeRefresh, user } = useAuth();
  // `calls` changes on every call event during a live call (App.js), so this
  // page re-renders on each one. Only recount the agents when calls change.
  const knownAgents = useMemo(() => getKnownAgents(calls), [calls]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [filter, setFilter] = useState('all');
  const [whatsappStatus, setWhatsappStatus] = useState('idle'); // idle | sending | sent | error
  const [whatsappError, setWhatsappError] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const [newChatOpen, setNewChatOpen] = useState(false);
  // Numbers started via NewChatModal that don't have a real thread yet —
  // kept separate from `conversations` because that state gets replaced
  // wholesale on every poll (see fetchConversations below) with the server's
  // list, which only includes numbers that already have a whatsapp_messages
  // row. Without this, a just-started chat would vanish from the sidebar the
  // moment the next 10s poll landed, before the agent had sent anything.
  const [pendingChats, setPendingChats] = useState([]);
  // Open / Pending / Resolved workflow + assignment (see
  // whatsapp_conversation_state in server/db.js). Defaults to the Open
  // queue — every thread starts Open, so nothing disappears on first load.
  const [statusFilter, setStatusFilter] = useState('open');
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // all | me | unassigned | <user id>
  const [assignees, setAssignees] = useState([]);
  const [stateSaving, setStateSaving] = useState(false);
  const [stateError, setStateError] = useState('');
  // Message search (across every thread's message text), shown under the
  // chat list once the search box has 3+ characters.
  const [messageResults, setMessageResults] = useState([]);
  const [messageSearching, setMessageSearching] = useState(false);
  const [focusMessage, setFocusMessage] = useState(null);
  // Customer info panel: closed until the agent clicks ⓘ, and closes again
  // when switching chats.
  const [infoOpen, setInfoOpen] = useState(false);
  // In-chat search bar (inside WhatsAppChat), toggled by the header's
  // "Search chat" button; closes when switching to another chat.
  const [chatSearchOpen, setChatSearchOpen] = useState(false);
  // Stable, so a call event re-rendering this page skips the open chat
  // (WhatsAppChat is memoized).
  const closeChatSearch = useCallback(() => setChatSearchOpen(false), []);
  const [wallpaperPickerOpen, setWallpaperPickerOpen] = useState(false);
  // Header element the open chat renders "viewing now" / "Seen by" into
  // (WhatsAppChat's viewersSlot), so it doesn't take a row above the thread.
  // Set once by a callback ref; the header stays mounted across chats.
  const [viewersSlot, setViewersSlot] = useState(null);
  // Text the info panel wants in the chat composer ("Send to customer").
  const [draftInsert, setDraftInsert] = useState(null);

  const toggleInfo = () => setInfoOpen((open) => !open);

  useEffect(() => {
    authFetch('/api/whatsapp/assignees')
      .then((response) => response.json())
      .then((data) => setAssignees(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Error fetching WhatsApp assignees:', err));
    // eslint-disable-next-line
  }, []);

  const searchTerm = search.trim();
  useEffect(() => {
    if (searchTerm.length < 3) {
      setMessageResults([]);
      setMessageSearching(false);
      return undefined;
    }
    setMessageSearching(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await authFetch(`/api/whatsapp/search?q=${encodeURIComponent(searchTerm)}`);
        const data = await response.json();
        if (!cancelled) setMessageResults(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setMessageResults([]);
      } finally {
        if (!cancelled) setMessageSearching(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line
  }, [searchTerm]);

  // seq each list row was last set from (see whatsappSync.js), so a slow
  // full reload can't undo a newer single-row update; and the single-row
  // reloads in flight, one per thread at a time.
  const listRowSeqRef = useRef(new Map());
  const rowLoadsRef = useRef(new Map());
  const mergeConversations = (rows, { seq, full = false }) =>
    setConversations((current) =>
      mergeRows(current, rows, {
        seq,
        full,
        rowSeq: listRowSeqRef.current,
        keyOf: (row) => row.number,
        compare: byLatestMessage,
      })
    );

  // A pending chat graduates to "real" the moment it actually gets a message
  // (it'll show up in `rows` then) — drop it here so it doesn't linger as a
  // duplicate once the real row exists.
  const dropGraduatedPendingChats = (rows) =>
    setPendingChats((previous) =>
      previous.filter(
        (pending) => !rows.some((conversation) => normalizeNumber(conversation.number) === normalizeNumber(pending.number))
      )
    );

  // `silent` skips the full-page loading state for the background
  // auto-refresh poll, same reasoning as DailyOrders.js's fetchOrders.
  const fetchConversations = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await authFetch('/api/whatsapp/conversations');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load WhatsApp conversations');
      const fetched = Array.isArray(data) ? data : [];
      mergeConversations(fetched, { seq: readSeq(response), full: true });
      dropGraduatedPendingChats(fetched);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load WhatsApp conversations');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Reloads one thread's row after a live event (new message, tick, status
  // change), instead of the whole list. A second event for the same thread
  // while its reload is running just runs it once more afterwards. Failures
  // are left to the 10s full reload.
  const fetchConversationRow = async (number) => {
    const running = rowLoadsRef.current.get(number);
    if (running) {
      running.again = true;
      return;
    }
    const load = { again: true };
    rowLoadsRef.current.set(number, load);
    try {
      while (load.again) {
        load.again = false;
        const response = await authFetch(`/api/whatsapp/conversations?number=${encodeURIComponent(number)}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to load WhatsApp conversation');
        const rows = Array.isArray(data) ? data.filter((row) => row.number === number) : [];
        if (rows.length) {
          mergeConversations(rows, { seq: readSeq(response) });
          dropGraduatedPendingChats(rows);
        }
      }
    } catch {
      // The next event or the 10s full reload catches up.
    } finally {
      rowLoadsRef.current.delete(number);
    }
  };

  useEffect(() => {
    fetchConversations();
    const unsubscribe = subscribeRefresh((event) => {
      if (!isRefreshFor(event, 'whatsapp')) return;
      // No details (an older server): reload the whole list, as before.
      if (!event?.type || !event.customerNumber) {
        fetchConversations({ silent: true });
        return;
      }
      // Notes aren't shown in the list.
      if (event.type === 'note_created' || event.type === 'note_deleted') return;
      fetchConversationRow(event.customerNumber);
    });
    const interval = setInterval(() => fetchConversations({ silent: true }), AUTO_REFRESH_MS);
    return () => {
      unsubscribe();
      clearInterval(interval);
    };
    // eslint-disable-next-line
  }, []);

  // Optimistically zeroes this thread's badge the moment it's opened, rather
  // than waiting up to AUTO_REFRESH_MS for the next poll to reflect the
  // POST /read call WhatsAppChat.js fires once it loads — that POST is still
  // what actually persists the read state (so it stays cleared for every
  // agent, not just this browser tab), this just avoids the visible lag.
  const handleSelect = (number, messageId = null) => {
    setSelectedNumber(number);
    setFocusMessage(messageId ? { id: messageId } : null);
    setStateError('');
    setChatSearchOpen(false);
    setInfoOpen(false);
    setWhatsappStatus('idle');
    setWhatsappError('');
    setEditingName(false);
    setNameError('');
    setConversations((previous) =>
      previous.map((conversation) => (conversation.number === number ? { ...conversation, unread_count: 0 } : conversation))
    );
  };
  // Stable, for the memoized list rows; always runs this render's handleSelect.
  const handleSelectRef = useRef(handleSelect);
  handleSelectRef.current = handleSelect;
  const selectConversation = useCallback((number) => handleSelectRef.current(number), []);

  // Opens the chat panel for a number typed into NewChatModal. If it already
  // has a thread (agent just didn't spot it in the list), selects that
  // instead of creating a duplicate row. Otherwise saves the optional name
  // (upsertCustomer creates the customers row if it doesn't exist yet — see
  // PUT /api/customers/:number) and adds it to `pendingChats` so `selected`
  // below finds it immediately, without waiting for — and without getting
  // wiped out by — the next /api/whatsapp/conversations poll (see
  // `pendingChats`'s own comment for why that split exists).
  const handleStartNewChat = async (rawNumber, rawName) => {
    const number = normalizeNumber(rawNumber);
    const existing = conversations.find((conversation) => normalizeNumber(conversation.number) === number);
    if (existing) {
      handleSelect(existing.number);
      setNewChatOpen(false);
      return;
    }
    if (rawName) {
      const response = await authFetch(`/api/customers/${encodeURIComponent(number)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: rawName }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save customer name');
      }
    }
    setPendingChats((previous) => [{ number, name: rawName || null }, ...previous]);
    handleSelect(number);
    setNewChatOpen(false);
  };

  // Opens a chat handed over from another page (the Email page's customer
  // panel), the same way a number typed into NewChatModal opens: the
  // existing thread if there is one, otherwise a not-yet-messaged chat.
  useEffect(() => {
    if (!openNumber || loading) return;
    const number = normalizeNumber(openNumber);
    const existing = conversations.find((conversation) => normalizeNumber(conversation.number) === number);
    if (existing) {
      handleSelect(existing.number);
    } else {
      setPendingChats((previous) =>
        previous.some((pending) => normalizeNumber(pending.number) === number) ? previous : [{ number, name: null }, ...previous]
      );
      handleSelect(number);
    }
    onOpenNumberConsumed?.();
    // eslint-disable-next-line
  }, [openNumber, loading]);

  const startEditName = () => {
    setNameDraft(selected?.name || '');
    setNameError('');
    setEditingName(true);
  };

  // A conversation row (getWhatsappConversations, server/db.js) only carries
  // `name` — not the rest of the customers row — so a plain PUT with just the
  // new name would null out any alt phone/email/city/etc already saved from
  // the call-history modal (upsertCustomer overwrites every field it's given,
  // COALESCE only protects order_number). Fetching the full profile first and
  // sending it back unchanged alongside the new name avoids that.
  const saveName = async () => {
    if (!selected) return;
    const trimmed = nameDraft.trim();
    setNameSaving(true);
    setNameError('');
    try {
      const profileResponse = await authFetch(`/api/customers/${encodeURIComponent(selected.number)}`);
      const profile = await profileResponse.json();
      if (!profileResponse.ok) throw new Error(profile.error || 'Failed to load customer profile');
      const response = await authFetch(`/api/customers/${encodeURIComponent(selected.number)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmed,
          isNew: profile.is_new,
          altPhone: profile.alt_phone,
          whatsappNumber: profile.whatsapp_number,
          email: profile.email,
          city: profile.city,
          state: profile.state,
          orderNumber: profile.order_number,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save name');
      setConversations((previous) =>
        previous.map((conversation) =>
          conversation.number === selected.number ? { ...conversation, name: data.name } : conversation
        )
      );
      // The name edit may be on a still-pending (not-yet-messaged) chat,
      // which lives in pendingChats rather than conversations — see
      // displayConversations above.
      setPendingChats((previous) =>
        previous.map((pending) => (pending.number === selected.number ? { ...pending, name: data.name } : pending))
      );
      setEditingName(false);
    } catch (err) {
      setNameError(err.message || 'Failed to save name');
    } finally {
      setNameSaving(false);
    }
  };

  // Same fixed-template send as CallHistoryModal.js's "WhatsApp Greeting"
  // button (POST /api/customers/:number/send-whatsapp) — offered here too so
  // an agent outside the customer's 24h session window (see WhatsAppChat.js's
  // sessionOpen hint) has a way to reach them from this page as well, without
  // having to go find the same customer's call history first.
  const handleSendWhatsappGreeting = async () => {
    if (!selectedNumber) return;
    setWhatsappStatus('sending');
    setWhatsappError('');
    try {
      const response = await authFetch(`/api/customers/${encodeURIComponent(selectedNumber)}/send-whatsapp`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Send failed');
      setWhatsappStatus('sent');
    } catch (err) {
      setWhatsappStatus('error');
      setWhatsappError(err.message || 'Send failed');
    }
  };

  // Saves a status and/or assignee change for the selected thread, then
  // mirrors it into local state so the list updates without waiting for the
  // next poll. `changes` is { status?, assignedUserId? }.
  const updateConversationState = async (changes) => {
    if (!selected) return;
    setStateSaving(true);
    setStateError('');
    try {
      const response = await authFetch(`/api/whatsapp/conversations/${encodeURIComponent(selected.number)}/state`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changes),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update chat');
      const patch = {
        status: data.status,
        assigned_user_id: data.assigned_user_id,
        assigned_name: data.assigned_name,
      };
      setConversations((previous) =>
        previous.map((conversation) => (conversation.number === selected.number ? { ...conversation, ...patch } : conversation))
      );
      setPendingChats((previous) =>
        previous.map((pending) => (pending.number === selected.number ? { ...pending, ...patch } : pending))
      );
    } catch (err) {
      setStateError(err.message || 'Failed to update chat');
    } finally {
      setStateSaving(false);
    }
  };

  // `conversations` (server-backed) plus any not-yet-messaged `pendingChats`,
  // deduped by number — a pending entry only shows up here until the poll
  // above sees a real row for it and drops it from `pendingChats`.
  const displayConversations = useMemo(() => {
    const extras = pendingChats
      .filter((pending) => !conversations.some((conversation) => normalizeNumber(conversation.number) === normalizeNumber(pending.number)))
      .map((pending) => ({
        number: pending.number,
        name: pending.name,
        last_message_at: null,
        last_body: null,
        last_message_type: null,
        last_direction: null,
        unread_count: 0,
        status: pending.status || 'open',
        assigned_user_id: pending.assigned_user_id ?? null,
        assigned_name: pending.assigned_name ?? null,
      }));
    return [...extras, ...conversations];
  }, [conversations, pendingChats]);

  const unreadCount = useMemo(
    () => displayConversations.filter((conversation) => conversation.unread_count > 0).length,
    [displayConversations]
  );

  // Most saved numbers are Indian (+91); a conversation on any other country
  // code is flagged so agents notice it's not a regular local customer,
  // same isInternationalNumber split IncomingCalls.js/AbandonedCarts.js use.
  const internationalCount = useMemo(
    () => displayConversations.filter((conversation) => isInternationalNumber(conversation.number)).length,
    [displayConversations]
  );

  // A new message from an international number is easy to miss inside the
  // plain "Unread" filter (buried among regular domestic threads) and the
  // plain "International" filter (mixed in with ones already read) — this
  // is the subset an agent's attention should actually go to. It empties out
  // the moment the thread is opened (handleSelect zeroes unread_count), at
  // which point that conversation is just a normal, already-read entry in
  // the International filter — nothing further to track.
  const unreadInternationalCount = useMemo(
    () =>
      displayConversations.filter(
        (conversation) => conversation.unread_count > 0 && isInternationalNumber(conversation.number)
      ).length,
    [displayConversations]
  );

  const matchesAssignee = (conversation) =>
    assigneeFilter === 'all'
      ? true
      : assigneeFilter === 'unassigned'
      ? !conversation.assigned_user_id
      : assigneeFilter === 'me'
      ? conversation.assigned_user_id === user?.id
      : String(conversation.assigned_user_id) === assigneeFilter;

  // Per-status counts for the status tabs, within the current assignee
  // filter (so "Mine" shows how many of MY chats are open/pending/resolved).
  const statusCounts = useMemo(() => {
    const counts = { open: 0, pending: 0, resolved: 0 };
    displayConversations.forEach((conversation) => {
      if (!matchesAssignee(conversation)) return;
      const status = conversation.status || 'open';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
    // eslint-disable-next-line
  }, [displayConversations, assigneeFilter, user]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const base = (
      filter === 'unread'
        ? displayConversations.filter((conversation) => conversation.unread_count > 0)
        : filter === 'international'
        ? displayConversations.filter((conversation) => isInternationalNumber(conversation.number))
        : filter === 'unread-international'
        ? displayConversations.filter(
            (conversation) => conversation.unread_count > 0 && isInternationalNumber(conversation.number)
          )
        : displayConversations
    ).filter(
      (conversation) =>
        matchesAssignee(conversation) && (statusFilter === 'all' || (conversation.status || 'open') === statusFilter)
    );
    if (!term) return base;
    return base.filter(
      (conversation) =>
        (conversation.name || '').toLowerCase().includes(term) ||
        (conversation.number || '').toLowerCase().includes(term)
    );
    // eslint-disable-next-line
  }, [displayConversations, search, filter, statusFilter, assigneeFilter, user]);

  // A new search or filter starts again from the top LIST_PAGE rows.
  const [listLimit, setListLimit] = useState(LIST_PAGE);
  useEffect(() => {
    setListLimit(LIST_PAGE);
  }, [search, filter, statusFilter, assigneeFilter]);
  const shownConversations = useMemo(
    () => (filtered.length > listLimit ? filtered.slice(0, listLimit) : filtered),
    [filtered, listLimit]
  );
  const showMoreConversations = () => {
    if (filtered.length > listLimit) setListLimit((limit) => limit + LIST_PAGE);
  };

  const selected = displayConversations.find((conversation) => conversation.number === selectedNumber) || null;

  if (loading) {
    return <div className="loading-state">Loading…</div>;
  }

  return (
    <div className="whatsapp-overview">
      <div className="whatsapp-overview-sidebar">
        <div className="whatsapp-overview-search-row">
          <div className="search-input-wrap whatsapp-overview-search">
            <SearchIcon className="search-input-icon" width={15} height={15} />
            <input
              type="text"
              className="search-input"
              placeholder="Search name, number or messages"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search WhatsApp conversations"
            />
          </div>
          <button type="button" className="btn btn-secondary whatsapp-overview-new-chat-btn" onClick={() => setNewChatOpen(true)}>
            New Chat
          </button>
        </div>
        <div className="whatsapp-overview-filters">
          <button
            type="button"
            className={`whatsapp-overview-filter-btn ${filter === 'all' ? 'whatsapp-overview-filter-btn-active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            type="button"
            className={`whatsapp-overview-filter-btn ${filter === 'unread' ? 'whatsapp-overview-filter-btn-active' : ''}`}
            onClick={() => setFilter('unread')}
          >
            Unread{unreadCount > 0 ? ` (${unreadCount})` : ''}
          </button>
          <button
            type="button"
            className={`whatsapp-overview-filter-btn ${filter === 'international' ? 'whatsapp-overview-filter-btn-active' : ''}`}
            onClick={() => setFilter('international')}
          >
            International{internationalCount > 0 ? ` (${internationalCount})` : ''}
          </button>
          <button
            type="button"
            className={`whatsapp-overview-filter-btn whatsapp-overview-filter-btn-attention ${
              filter === 'unread-international' ? 'whatsapp-overview-filter-btn-active' : ''
            }`}
            onClick={() => setFilter('unread-international')}
          >
            Unread International{unreadInternationalCount > 0 ? ` (${unreadInternationalCount})` : ''}
          </button>
        </div>
        <div className="wa-status-tabs" role="tablist" aria-label="Chat status">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={statusFilter === option.value}
              className={`wa-status-tab wa-status-tab-${option.value} ${statusFilter === option.value ? 'wa-status-tab-active' : ''}`}
              onClick={() => setStatusFilter(option.value)}
            >
              {option.label}
              <span className="wa-status-tab-count">{statusCounts[option.value] || 0}</span>
            </button>
          ))}
          <button
            type="button"
            role="tab"
            aria-selected={statusFilter === 'all'}
            className={`wa-status-tab ${statusFilter === 'all' ? 'wa-status-tab-active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All
          </button>
        </div>
        <div className="wa-assignee-filter">
          <span className="wa-assignee-filter-label">Assigned to</span>
          <SelectMenu
            className="wa-assignee-select"
            value={assigneeFilter}
            onChange={setAssigneeFilter}
            ariaLabel="Filter chats by assignee"
            icon={<UserIcon width={13} height={13} />}
            options={[
              { value: 'all', label: 'Anyone' },
              { value: 'me', label: 'Me' },
              { value: 'unassigned', label: 'Unassigned' },
              ...assignees
                .filter((assignee) => assignee.id !== user?.id)
                .map((assignee) => ({ value: String(assignee.id), label: assignee.display_name })),
            ]}
          />
        </div>
        {error && <div className="call-feedback call-feedback-error whatsapp-overview-error">{error}</div>}
        <div
          className="whatsapp-overview-list"
          onScroll={(event) => {
            const el = event.currentTarget;
            if (el.scrollTop + el.clientHeight > el.scrollHeight - LIST_MORE_WITHIN_PX) showMoreConversations();
          }}
        >
          {searchTerm.length >= 3 && <div className="wa-list-heading">Chats</div>}
          {filtered.length === 0 ? (
            <p className="whatsapp-overview-empty-list">
              {searchTerm
                ? 'No chats match in this view.'
                : statusFilter !== 'all' && statusFilter !== 'open'
                ? `No ${statusFilter} chats.`
                : filter === 'unread'
                ? 'No unread conversations.'
                : filter === 'international'
                ? 'No international conversations.'
                : filter === 'unread-international'
                ? 'No unread international conversations.'
                : 'No WhatsApp conversations yet.'}
            </p>
          ) : (
            shownConversations.map((conversation) => (
              <ConversationRow
                key={conversation.number}
                conversation={conversation}
                active={conversation.number === selectedNumber}
                userId={user?.id}
                onSelect={selectConversation}
              />
            ))
          )}
          {filtered.length > shownConversations.length && (
            <button type="button" className="link-btn wa-list-more" onClick={showMoreConversations}>
              Show more chats ({(filtered.length - shownConversations.length).toLocaleString()})
            </button>
          )}
          {searchTerm.length >= 3 && (
            <>
              <div className="wa-list-heading">
                Messages{!messageSearching && messageResults.length > 0 ? ` (${messageResults.length})` : ''}
              </div>
              {messageSearching ? (
                <p className="whatsapp-overview-empty-list">Searching…</p>
              ) : messageResults.length === 0 ? (
                <p className="whatsapp-overview-empty-list">No messages contain “{searchTerm}”.</p>
              ) : (
                messageResults.map((result) => (
                  <button
                    type="button"
                    key={result.id}
                    className={`whatsapp-overview-item wa-message-result ${
                      focusMessage?.id === result.id ? 'whatsapp-overview-item-active' : ''
                    }`}
                    onClick={() => handleSelect(result.customer_number, result.id)}
                  >
                    <span className="whatsapp-overview-item-body">
                      <span className="whatsapp-overview-item-top">
                        <span className="whatsapp-overview-item-name">
                          {result.customer_name || result.customer_number}
                        </span>
                        <span className="whatsapp-overview-item-time">{listTimestamp(result.created_at)}</span>
                      </span>
                      <span className="wa-message-result-snippet">
                        {result.direction === 'outbound' ? 'You: ' : ''}
                        {searchSnippet(result.body || result.media_filename, searchTerm)}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </>
          )}
        </div>
      </div>

      <div className="whatsapp-overview-main">
        {!selected ? (
          <div className="whatsapp-overview-placeholder">
            <WhatsAppIcon width={48} height={48} />
            <p>Select a conversation to view messages.</p>
          </div>
        ) : (
          <>
            <div className="whatsapp-overview-header">
              <span className="whatsapp-overview-avatar">{initials(selected.name, selected.number)}</span>
              <div className="whatsapp-overview-header-info">
                {editingName ? (
                  <span className="whatsapp-overview-name-edit">
                    <input
                      type="text"
                      value={nameDraft}
                      onChange={(event) => setNameDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') saveName();
                        if (event.key === 'Escape') setEditingName(false);
                      }}
                      placeholder="Customer name"
                      autoFocus
                      disabled={nameSaving}
                    />
                    <button type="button" className="link-btn" onClick={saveName} disabled={nameSaving}>
                      {nameSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button type="button" className="link-btn" onClick={() => setEditingName(false)} disabled={nameSaving}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  <span className="whatsapp-overview-header-name">
                    {selected.name || selected.number}
                    <button type="button" className="link-btn" onClick={startEditName}>
                      {selected.name ? 'Edit' : 'Add name'}
                    </button>
                  </span>
                )}
                {nameError && <span className="call-feedback call-feedback-error">{nameError}</span>}
                <span className="whatsapp-overview-header-number">
                  {selected.number}
                  {isInternationalNumber(selected.number) && (
                    <span className="whatsapp-overview-badge-international">{internationalLabel(selected.number)}</span>
                  )}
                </span>
              </div>
              <div className="wa-conv-status" role="group" aria-label="Chat status">
                {STATUS_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`wa-conv-status-btn wa-conv-status-btn-${option.value} ${
                      (selected.status || 'open') === option.value ? 'wa-conv-status-btn-active' : ''
                    }`}
                    aria-pressed={(selected.status || 'open') === option.value}
                    disabled={stateSaving}
                    onClick={() => updateConversationState({ status: option.value })}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <div className="wa-conv-assign">
                <SelectMenu
                  className="wa-assignee-select"
                  value={selected.assigned_user_id ? String(selected.assigned_user_id) : ''}
                  disabled={stateSaving}
                  onChange={(value) => updateConversationState({ assignedUserId: value ? Number(value) : null })}
                  ariaLabel="Assign this chat"
                  icon={<UserIcon width={13} height={13} />}
                  options={[
                    { value: '', label: 'Unassigned' },
                    ...assignees.map((assignee) => ({
                      value: String(assignee.id),
                      label: `${assignee.display_name}${assignee.id === user?.id ? ' (me)' : ''}`,
                    })),
                  ]}
                />
              </div>
              {selected.assigned_user_id !== user?.id && user?.id && (
                <button
                  type="button"
                  className="link-btn"
                  disabled={stateSaving}
                  onClick={() => updateConversationState({ assignedUserId: user.id })}
                >
                  Assign to me
                </button>
              )}
              {stateError && <span className="call-feedback call-feedback-error">{stateError}</span>}
              <div className="wa-topbar-viewers" ref={setViewersSlot} />
              <div className="wa-topbar-actions">
                <CallButton agents={knownAgents} customerNumber={selected.number} onCallTriggered={() => {}} />
                <span className="wa-topbar-divider" aria-hidden="true" />
                {onCreateInternationalOrder && (
                  <button
                    type="button"
                    className="wa-topbar-btn"
                    onClick={() => onCreateInternationalOrder(selected.number)}
                    title="Start an international order for this customer"
                    aria-label="International order"
                  >
                    <GlobeIcon width={15} height={15} />
                    <span className="wa-topbar-btn-label">International order</span>
                  </button>
                )}
                <button
                  type="button"
                  className="wa-topbar-btn"
                  onClick={handleSendWhatsappGreeting}
                  disabled={whatsappStatus === 'sending'}
                  title="Sends a fixed WhatsApp greeting to this customer"
                  aria-label="WhatsApp Greeting"
                >
                  <MessageIcon width={15} height={15} />
                  <span className="wa-topbar-btn-label">
                    {whatsappStatus === 'sending' ? 'Sending…' : 'WhatsApp Greeting'}
                  </span>
                </button>
                {whatsappStatus === 'sent' && <span className="call-feedback call-feedback-ok">Sent</span>}
                {whatsappStatus === 'error' && (
                  <span className="call-feedback call-feedback-error">{whatsappError}</span>
                )}
                <button
                  type="button"
                  className={`wa-topbar-btn ${chatSearchOpen ? 'wa-topbar-btn-active' : ''}`}
                  onClick={() => setChatSearchOpen((open) => !open)}
                  aria-pressed={chatSearchOpen}
                  title="Search in this chat"
                  aria-label="Search chat"
                >
                  <SearchIcon width={15} height={15} />
                </button>
                <button
                  type="button"
                  className="wa-topbar-btn"
                  onClick={() => setWallpaperPickerOpen(true)}
                  title="Change your chat wallpaper"
                  aria-label="Chat wallpaper"
                >
                  <span aria-hidden="true">🎨</span>
                </button>
                <button
                  type="button"
                  className={`wa-topbar-btn ${infoOpen ? 'wa-topbar-btn-active' : ''}`}
                  onClick={toggleInfo}
                  aria-pressed={infoOpen}
                  title={infoOpen ? 'Hide customer info' : 'Show customer info'}
                  aria-label="Customer info"
                >
                  <InfoIcon width={15} height={15} />
                </button>
              </div>
            </div>
            <div className="whatsapp-overview-chat">
              <WhatsAppChat
                customerNumber={selected.number}
                customerName={selected.name}
                focusMessage={focusMessage}
                draftInsert={draftInsert}
                searchOpen={chatSearchOpen}
                onSearchClose={closeChatSearch}
                viewersSlot={viewersSlot}
              />
            </div>
          </>
        )}
      </div>

      {selected && infoOpen && (
        <WhatsAppCustomerPanel
          customerNumber={selected.number}
          calls={calls}
          onSendToChat={(text) => setDraftInsert({ text })}
        />
      )}

      {newChatOpen && <NewChatModal onClose={() => setNewChatOpen(false)} onSubmit={handleStartNewChat} />}
      {wallpaperPickerOpen && <WhatsAppWallpaperPicker onClose={() => setWallpaperPickerOpen(false)} />}
    </div>
  );
}

export default WhatsAppOverview;
